import {
  AnalysisResult,
  CoreAnalysisEngine,
  RecordingResponse,
} from "./coreAnalysisEngine";
import { ReactElement } from "react";
import { TFunction } from "i18next";
import { ResultDashboard } from "../components/molecules/ResultDashboard";
import {
  AnalysisEngineType,
  EngineType,
  ResultDashboardData,
  ResultDiagramIndex,
  ResultMainParam,
  ResultSubParam,
  ResultSupplementaryInfo,
} from "../types";
import { ResultMi2Demo } from "../components/molecules/ResultMi2Demo";
import { ResultMi2TFH } from "../components/molecules/ResultMi2TFH";

export type MI2_DATA = {
  success: boolean;
  zh_value: number;
  cpf_value: number;
  hurst_exponent: number;
  zero_cross_rate: number;
  wav_filename: string;
  start_time: number;
  end_time: number;
};

export type Mi2AnalysisResult = AnalysisResult<"mi2">;
export type Mi2DetailedAnalysisResult = MI2_DATA[];

const DEFAULT_DASHBOARD_MAIN_PARAM: ResultMainParam = {
  title: "Result.Mi2_score",
  // border: "solid 3px #F2CA95",
  value: undefined,
};

const SUB_PARAMS_TITLE_LIST = ["Result.Mi2_cpf", "Result.Mi2_zh"];

const SUPPLEMENTARY_INFO_TITLE_LIST = [
  "Result.analyze_files",
  "Result.analyze_utterances",
];

export function ConvertResult({
  result,
}: {
  result: Mi2AnalysisResult;
}): Record<string, number | string> {
  if (!result) {
    return {
      zh_score: "-",
      cpf_score: "-",
    };
  }
  const zh_score = Math.floor(result.analysis_result.zh_score * 100);
  const cpf_score = Math.floor(result.analysis_result.cpf_score * 100);
  return {
    zh_score: zh_score,
    cpf_score: cpf_score,
  };
}

interface EngineProps {
  _analysisType?: AnalysisEngineType | null;
}

export class Mi2Engine extends CoreAnalysisEngine<"mi2"> {
  _engineType: EngineType = "mi2";
  _analysisType: AnalysisEngineType | null = null;

  constructor(props?: EngineProps) {
    super("mi2");
    if (props && props._analysisType) {
      this._analysisType = props._analysisType;
    }
  }

  private static dashboardDiagramIndex(t: TFunction): ResultDiagramIndex[] {
    return [
      { width: "32px", title: "No" },
      { width: "48%", title: t("Result.table_phrase"), align: "left" },
      { width: "10%", title: "cpf" },
      { width: "10%", title: "z-h" },
      { width: "12%", title: "hurst" },
      { width: "10%", title: "zcr" },
      { width: "10%", title: "sec" },
    ];
  }

  private static errorDashboardData(t: TFunction): ResultDashboardData {
    return {
      mainParam: DEFAULT_DASHBOARD_MAIN_PARAM,
      subParams: [
        {
          title: SUB_PARAMS_TITLE_LIST[0],
          value: undefined,
        },
        {
          title: SUB_PARAMS_TITLE_LIST[1],
          value: undefined,
        },
      ],
      supplementaryInfo: [
        {
          title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
          value: undefined,
        },
        {
          title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
          value: undefined,
        },
      ],
      diagramIndex: this.dashboardDiagramIndex(t),
      diagramParams: [],
    };
  }

  private static convertToDashboardData(
    result: Mi2AnalysisResult,
    recording: RecordingResponse | null,
    t: TFunction,
  ): ResultDashboardData {
    const phraseByFilename = new Map(
      recording?.files.map((f) => [f.filename, f.phrase]) ?? [],
    );
    const mi2_data = (result.detailed_analysis_result ??
      []) as Mi2DetailedAnalysisResult;
    const diagramParams = CoreAnalysisEngine.generateResultDiagramParams(
      mi2_data,
      (param, recordId, _phraseIndex) => [
        { width: "32px", value: recordId, align: "left" },
        {
          width: "48%",
          value: phraseByFilename.get(param.wav_filename) ?? "Error",
          align: "left",
        },
        {
          width: "10%",
          value: Math.floor(param.cpf_value * 100),
        },
        {
          width: "10%",
          value: Math.floor(param.zh_value * 100),
        },
        {
          width: "12%",
          value: Math.floor(param.hurst_exponent * 100),
        },
        {
          width: "10%",
          value: Math.floor(param.zero_cross_rate * 100),
        },
        {
          width: "10%",
          value: ((param.end_time - param.start_time) / 1000).toFixed(1),
        },
      ],
    );
    const mainParam: ResultMainParam = {
      ...DEFAULT_DASHBOARD_MAIN_PARAM,
      value: result.analysis_result.mi2_result,
    };

    const subParams: ResultSubParam[] = [
      {
        title: SUB_PARAMS_TITLE_LIST[0],
        value: `${Math.floor(result.analysis_result.cpf_score * 100)}(${
          result.analysis_result.cpf_score >= 0.42 ? "Nega" : "Posi"
        })`,
        color: `${result.analysis_result.cpf_score >= 0.42 ? "black" : "red"}`,
      },
      {
        title: SUB_PARAMS_TITLE_LIST[1],
        value: `${Math.floor(result.analysis_result.zh_score * 100)}(${
          result.analysis_result.zh_score >= 0.34 ? "Nega" : "Posi"
        })`,
        color: `${result.analysis_result.zh_score >= 0.34 ? "black" : "red"}`,
      },
    ];

    const supplementaryInfo: ResultSupplementaryInfo[] = [
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[0],
        value: recording?.files.length ?? 0,
      },
      {
        title: SUPPLEMENTARY_INFO_TITLE_LIST[1],
        value: result.analysis_result.utterances,
      },
    ];

    return {
      mainParam,
      subParams,
      supplementaryInfo,
      diagramIndex: this.dashboardDiagramIndex(t),
      diagramParams,
    };
  }

  async renderResult(
    key: number,
    fileId: string,
    t: TFunction,
  ): Promise<ReactElement> {
    const result = await this.getResult(fileId);
    const recording = result ? await this.getRecording(fileId) : null;

    const dashboardData = result
      ? Mi2Engine.convertToDashboardData(result, recording, t)
      : Mi2Engine.errorDashboardData(t);

    if (this._analysisType === "Mi2Demo") {
      return <ResultMi2Demo key={key} result={dashboardData} />;
    }

    if (this._analysisType === "TFH") {
      return <ResultMi2TFH key={key} result={dashboardData} />;
    }

    return (
      <ResultDashboard
        key={key}
        title={t("Result.Mi2_analysis_result")}
        result={dashboardData}
      />
    );
  }
}
